/**
 * SOLID Review
 * S — Strong. One concern: the order in which the frontend's graph is made — construct, connect,
 *     check, build — and nothing else. The connectors are FrontendConnectors'; the concretes the
 *     platform's.
 * O — Good. A new component is one construction and one phase here.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. One function in, the assembled graph out, and the walk of it.
 * D — Good. The composition root: the four component classes, the connector table and the kernel.
 */
import { assertAssembled, describePorts, type IEntityPort, type IPortObserver } from '@ml3d/kernel';
import { ComponentFrontendDomain } from '../../domain/uml-components/ComponentFrontendDomain';
import { ComponentFrontendApplication } from '../../application/uml-components/ComponentFrontendApplication';
import {
  ComponentFrontendInfrastructure, type IConfigDepContractClients, type IConfigDepInfrastructureHost,
} from '../../infrastructure/uml-components/ComponentFrontendInfrastructure';
import { ComponentFrontendFramework } from '../uml-components/ComponentFramework';
import { FrontendConnectors } from './FrontendConnectors';

/** What the frontend graph is assembled from: the wire's clients, the host's resources, the observer. */
export interface IConfigDepFrontendPlatform {
  readonly clients: IConfigDepContractClients;
  readonly host: IConfigDepInfrastructureHost;
  readonly observer: IPortObserver;
  /** Where a half-wired graph is reported. A browser cannot refuse to boot, so it says so instead. */
  readonly report: (message: string) => void;
}

/** The four components the module-level assembly builds (the Framework since FE6a). */
export interface IEntityFrontendGraph {
  readonly domain: ComponentFrontendDomain;
  readonly application: ComponentFrontendApplication;
  readonly infrastructure: ComponentFrontendInfrastructure;
  readonly framework: ComponentFrontendFramework;
}

/** The Domain's model name: like the backend's, the flat Domain carries no MODEL_NAME of its own. */
const DOMAIN = 'ComponentFrontendDomain';

/**
 * THE FRONTEND'S ASSEMBLY (frontend strict-ports plan, FE1d, 2026-10-03) — the twin of the backend's
 * `BackendAssembly`, run ONCE, at module load, by the composition root (`adapterRegistry`), never in a
 * render:
 *
 *   1. construct Domain, Application, Infrastructure;
 *   2. connect Infrastructure's sockets (the wire, the Application's delivery lollipop);
 *   3. check Infrastructure, then `infrastructure.build()` — its lollipops exist;
 *   4. connect the Application's sockets (the Domain's decisions, Infrastructure's lollipops);
 *   5. check the Application, then `application.build()`;
 *   6. connect the Framework's two sockets (the Application's use cases, Infrastructure's view) and check
 *      it — since FE6a, when the Framework stopped connecting itself per provider mount.
 *
 * A CHECK THAT REPORTS, NOT ONE THAT REFUSES. The backend refuses to boot half-wired; a browser that
 * did would white-screen, which the frontend failure policy forbids (every socket degrades to its
 * Null provider). So a half-wired graph is REPORTED through `platform.report`, and the
 * production-wiring test is the gate that keeps one from shipping.
 */
export const FrontendAssembly = {
  assemble(platform: IConfigDepFrontendPlatform): IEntityFrontendGraph {
    const domain = new ComponentFrontendDomain();
    const application = new ComponentFrontendApplication();
    const infrastructure = new ComponentFrontendInfrastructure(platform.host);
    const connectors = new FrontendConnectors(platform.observer);
    const check = (ports: readonly IEntityPort[]): void => {
      try {
        assertAssembled(ports);
      } catch (err) {
        platform.report((err as Error).message);
      }
    };

    connectors.infrastructure(infrastructure, application, platform.clients);
    check(describePorts(ComponentFrontendInfrastructure.MODEL_NAME, infrastructure));
    infrastructure.build();
    connectors.application(application, infrastructure, domain);
    check(describePorts(ComponentFrontendApplication.MODEL_NAME, application));
    application.build();
    const framework = new ComponentFrontendFramework();
    connectors.framework(framework, application, infrastructure);
    check(describePorts(ComponentFrontendFramework.MODEL_NAME, framework));
    const graph: IEntityFrontendGraph = { domain, application, infrastructure, framework };
    return graph;
  },

  /** Every port of the four assembled components, in walk order — Domain, Application, Infrastructure, Framework. */
  portsOf(graph: IEntityFrontendGraph): IEntityPort[] {
    return [
      ...describePorts(DOMAIN, graph.domain),
      ...describePorts(ComponentFrontendApplication.MODEL_NAME, graph.application),
      ...describePorts(ComponentFrontendInfrastructure.MODEL_NAME, graph.infrastructure),
      ...describePorts(ComponentFrontendFramework.MODEL_NAME, graph.framework),
    ];
  },
};
